import * as XLSXStyle from 'xlsx-js-style';
import {
  INGRESO_POR_DIA_TITULO_DEFAULT,
  INGRESO_POR_DIA_COLUMNS,
  applyIngresoPorDiaColumnLabels,
  getIngresoPorDiaTotals,
  mergeIngresoPorDiaEncabezado,
  normalizeIngresoPorDiaRow,
} from './ingresoPorDiaReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

function applyCell(ws, r, c, value, style) {
  const addr = XLSXStyle.utils.encode_cell({ r, c });
  ws[addr] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
}

function mergeAcross(ws, r, fromC, toC) {
  if (!ws['!merges']) ws['!merges'] = [];
  ws['!merges'].push({ s: { r, c: fromC }, e: { r, c: toC } });
}

const toNum = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

export function downloadIngresoPorDiaExcelFile(rawRows, options = {}, fileName, sheetName) {
  const {
    encabezadoApi      = null,
    encabezadoFallback = null,
    reporteTitulo      = INGRESO_POR_DIA_TITULO_DEFAULT,
    apiTotales         = null,
    columnas           = null,
    vistaIngreso       = 'dia',
  } = options;

  const cols = applyIngresoPorDiaColumnLabels(columnas, INGRESO_POR_DIA_COLUMNS);
  const N_COLS    = cols.length;

  const list     = Array.isArray(rawRows) ? rawRows : [];
  const normRows = list.map(normalizeIngresoPorDiaRow);
  const totals   = getIngresoPorDiaTotals(normRows, apiTotales);

  const enc = mergeIngresoPorDiaEncabezado(encabezadoApi, encabezadoFallback);
  let nombreDisplay = enc.nombreComercial || '';
  if (enc.sucursalAppend && !nombreDisplay.toUpperCase().includes(enc.sucursalAppend.toUpperCase())) {
    nombreDisplay = nombreDisplay ? `${nombreDisplay} - ${enc.sucursalAppend.toUpperCase()}` : enc.sucursalAppend.toUpperCase();
  }

  /* ── Estilos ── */
  const center  = (rgb = 'FF0F172A', bold = false, sz = 10) => ({
    font: { bold, sz, color: { rgb } },
    alignment: { horizontal: 'center', wrapText: true },
  });
  const sTitulo = {
    font: { bold: true, sz: 13, color: { rgb: 'FF0F172A' } },
    alignment: { horizontal: 'center' },
    fill: { fgColor: { rgb: 'FFBFDBFE' } },
  };
  const sColHead = (halign = 'right') => ({
    font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { fgColor: { rgb: 'FF0F2F59' } },
    alignment: { horizontal: halign },
    border: { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
  });
  const sData = (bgColor, halign = 'right', money = true) => ({
    font: { color: { rgb: 'FF0F172A' } },
    fill: { fgColor: { rgb: bgColor } },
    alignment: { horizontal: halign },
    numFmt: money ? '#,##0.00' : '@',
  });
  const sTotalL = {
    font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { fgColor: { rgb: 'FF1E3A5F' } },
    alignment: { horizontal: 'left' },
    border: { top: { style: 'double', color: { rgb: 'FFBFDBFE' } } },
  };
  const sTotalV = {
    font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { fgColor: { rgb: 'FF1E3A5F' } },
    alignment: { horizontal: 'right' },
    numFmt: '#,##0.00',
    border: { top: { style: 'double', color: { rgb: 'FFBFDBFE' } } },
  };

  const ws = { '!ref': '', '!merges': [], '!cols': [] };
  let row = 0;

  /* ── Encabezado empresa ── */
  if (nombreDisplay) {
    applyCell(ws, row, 0, nombreDisplay.toUpperCase(), center('FF0F172A', true, 12));
    mergeAcross(ws, row, 0, N_COLS - 1);
    row++;
  }
  if (enc.domicilioFiscal) {
    applyCell(ws, row, 0, enc.domicilioFiscal, center('FF334155', false, 9));
    mergeAcross(ws, row, 0, N_COLS - 1);
    row++;
  }
  if (enc.rfc) {
    applyCell(ws, row, 0, `RFC: ${enc.rfc.toUpperCase()}`, center('FF334155', false, 9));
    mergeAcross(ws, row, 0, N_COLS - 1);
    row++;
  }

  applyCell(ws, row, 0, reporteTitulo.toUpperCase(), sTitulo);
  mergeAcross(ws, row, 0, N_COLS - 1);
  row++;

  if (enc.periodoTexto) { applyCell(ws, row, 0, enc.periodoTexto, center('FF475569', false, 9)); mergeAcross(ws, row, 0, N_COLS - 1); row++; }
  const filtrosAoa = [];
  appendFiltrosSucursalesCajasExcel(filtrosAoa, enc, {});
  filtrosAoa.forEach(([line]) => {
    applyCell(ws, row, 0, line, center('FF475569', false, 9));
    mergeAcross(ws, row, 0, N_COLS - 1);
    row++;
  });
  if (enc.turnosTexto)  { applyCell(ws, row, 0, `Turnos: ${enc.turnosTexto}`, center('FF475569', false, 9)); mergeAcross(ws, row, 0, N_COLS - 1); row++; }

  row++; /* separador */

  /* ── Encabezado de columnas ── */
  cols.forEach((c, ci) => {
    applyCell(ws, row, ci, c.label, sColHead(c.align === 'right' ? 'right' : 'left'));
  });
  row++;

  /* ── Filas de datos (omitidas en vista "total") ── */
  if (vistaIngreso !== 'total') {
    normRows.forEach((r, ri) => {
      const bg = ri % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
      cols.forEach((c, ci) => {
        if (c.kind === 'money') {
          applyCell(ws, row, ci, toNum(r[c.id]), sData(bg, 'right', true));
        } else {
          applyCell(ws, row, ci, String(r[c.id] ?? ''), sData(bg, 'left', false));
        }
      });
      row++;
    });
  }

  /* ── Fila de totales ── */
  cols.forEach((c, ci) => {
    if (ci === 0) {
      applyCell(ws, row, ci, 'TOTAL', sTotalL);
    } else if (c.total) {
      applyCell(ws, row, ci, toNum(totals[c.id]), sTotalV);
    } else {
      applyCell(ws, row, ci, '', sTotalL);
    }
  });
  row++;

  ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row - 1, c: N_COLS - 1 } });

  /* Anchos de columna */
  const wchMap = {
    fecha:      20, total:  16, cortesias: 14, venta: 16,
    efectivo:   14, tarjetas: 14, otros:  14, sueldos: 12,
    gastos:     12, final:  16, branchName: 36,
  };
  ws['!cols'] = cols.map((c) => ({ wch: wchMap[c.id] ?? 14 }));

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName || 'Ingresos por Día');
  XLSXStyle.writeFile(wb, fileName || 'ingresos-por-dia.xlsx');
}
